import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, CmsHeroSlide } from '@/lib/supabase';

const FALLBACK_SLIDES: CmsHeroSlide[] = [
  {
    id: 1,
    title: '技术驱动创新',
    subtitle: '智能赋能未来',
    description: '福建龙创未来股份有限公司',
    image_url: 'https://public.readdy.ai/ai/img_res/19c710b2e4ea1b5f1f600d8ca8332881.jpg',
    align: 'left',
    small_title: false,
    is_published: true,
    sort_order: 1,
    created_at: '',
    updated_at: '',
  },
];

export default function HeroCarousel() {
  const [slides, setSlides] = useState<CmsHeroSlide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    supabase
      .from('cms_hero_slides')
      .select('*')
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setSlides(data as CmsHeroSlide[]);
        } else {
          setSlides(FALLBACK_SLIDES);
        }
      });
  }, []);

  const startTimer = useCallback((len: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (len <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % len);
    }, 5000);
  }, []);

  useEffect(() => {
    if (slides.length > 0) {
      startTimer(slides.length);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [slides.length, startTimer]);

  const goToSlide = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    startTimer(slides.length);
    setTimeout(() => setIsAnimating(false), 700);
  };

  const goPrev = () => goToSlide((currentSlide - 1 + slides.length) % slides.length);
  const goNext = () => goToSlide((currentSlide + 1) % slides.length);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) diff > 0 ? goNext() : goPrev();
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (slides.length === 0) {
    return (
      <section id="home" className="relative h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
      </section>
    );
  }

  return (
    <section
      id="home"
      className="relative h-screen overflow-hidden select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={slide.image_url}
              alt={slide.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          </div>

          {/* Content */}
          <div className="relative h-full flex items-center overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full">
              <div
                className={`w-full max-w-2xl text-center mx-auto ${
                  slide.align === 'right'
                    ? 'lg:ml-auto lg:text-right lg:mr-0'
                    : slide.align === 'left'
                    ? 'lg:mx-0 lg:text-left'
                    : 'lg:mx-auto lg:text-center'
                }`}
              >
                {/* Tag */}
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full mb-4 sm:mb-6 max-w-full overflow-hidden">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 inline-block flex-shrink-0"></span>
                  <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wider uppercase truncate">
                    龙创未来 · AI 科技
                  </span>
                </div>

                {/* Title */}
                <div className="mb-3 sm:mb-4">
                  <div
                    className="font-bold text-white leading-tight block whitespace-nowrap"
                    style={{
                      fontSize: slide.small_title ? 'clamp(1.5rem, 4vw, 3.2rem)' : 'clamp(2.2rem, 7vw, 5.5rem)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {slide.title}
                  </div>
                  <div
                    className="font-extrabold text-teal-400 leading-tight block whitespace-nowrap"
                    style={{
                      fontSize: slide.small_title ? 'clamp(1.5rem, 4vw, 3.2rem)' : 'clamp(2.2rem, 7vw, 5.5rem)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {slide.subtitle}
                  </div>
                </div>

                {/* Description */}
                <p
                  className="text-white/85 mb-7 sm:mb-10 font-light"
                  style={{ fontSize: 'clamp(1rem, 2.5vw, 1.5rem)', lineHeight: 1.7 }}
                >
                  {slide.description}
                </p>

                {/* Buttons */}
                <div
                  className={`flex flex-row flex-wrap gap-2 sm:gap-3 justify-center ${
                    slide.align === 'right'
                      ? 'lg:justify-end'
                      : slide.align === 'left'
                      ? 'lg:justify-start'
                      : 'lg:justify-center'
                  }`}
                >
                  <a
                    href="#business"
                    className="px-5 sm:px-7 py-2.5 sm:py-3 border border-white/70 text-white text-xs sm:text-sm font-medium rounded-full hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                  >
                    了解更多
                  </a>
                  <a
                    href="#contact"
                    className="px-5 sm:px-7 py-2.5 sm:py-3 bg-teal-500 hover:bg-teal-400 text-white text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    联系我们
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Badge - only on first slide */}
          {index === 0 && (
            <div className="absolute bottom-24 left-4 sm:left-8 lg:left-12 z-20 flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-md px-4 sm:px-5 py-2 sm:py-2.5 rounded-full border border-white/20">
              <div className="flex -space-x-2">
                {['ri-building-line', 'ri-government-line', 'ri-hospital-line'].map((icon, i) => (
                  <div
                    key={i}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-white flex items-center justify-center ${
                      i === 0 ? 'bg-teal-500' : i === 1 ? 'bg-emerald-500' : 'bg-cyan-600'
                    }`}
                  >
                    <i className={`${icon} text-white text-xs sm:text-sm`}></i>
                  </div>
                ))}
              </div>
              <span className="text-white text-xs sm:text-sm font-medium">已服务 200+ 政企客户</span>
            </div>
          )}
        </div>
      ))}

      {/* Left Arrow */}
      {slides.length > 1 && (
        <button
          onClick={goPrev}
          className="absolute left-3 sm:left-5 lg:left-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-200 cursor-pointer"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-xl sm:text-2xl"></i>
        </button>
      )}

      {/* Right Arrow */}
      {slides.length > 1 && (
        <button
          onClick={goNext}
          className="absolute right-3 sm:right-5 lg:right-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-200 cursor-pointer"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-xl sm:text-2xl"></i>
        </button>
      )}

      {/* Bottom Controls */}
      <div className="absolute bottom-7 left-0 right-0 z-30 flex flex-col items-center gap-3">
        {slides.length > 1 && (
          <div className="flex items-center gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-400 cursor-pointer ${
                  index === currentSlide ? 'bg-teal-400 w-8' : 'bg-white/40 hover:bg-white/70 w-3'
                }`}
                aria-label={`切换到第${index + 1}张`}
              />
            ))}
          </div>
        )}
        <div className="flex flex-col items-center gap-1 animate-bounce opacity-70">
          <i className="ri-arrow-down-line text-white text-lg"></i>
        </div>
      </div>
    </section>
  );
}
